import moment from 'moment';

export function nomeAnuncio(item) {
    const texto = item?.headline || item?.body;
    if (texto) return String(texto).trim();
    if (item?.source_id && item.source_id !== 'sem_id') return `Anúncio ${item.source_id}`;
    return 'Sem identificação';
}

export function rotuloDia(dia, { compact = false } = {}) {
    if (!dia) return '';
    const m = moment(dia, 'YYYY-MM-DD');
    if (!m.isValid()) return dia;
    if (compact) return m.format('DD');
    return m.format('DD/MM');
}

/** Rótulo completo para tooltip (ex.: "02/08/2026"). */
export function rotuloDiaCompleto(dia) {
    if (!dia) return '';
    const m = moment(dia, 'YYYY-MM-DD');
    return m.isValid() ? m.format('DD/MM/YYYY') : dia;
}

export function mascaraTelefone(phone) {
    if (!phone) return '—';
    const digits = String(phone).replace(/\D/g, '');
    if (digits.length < 8) return digits;
    return `${digits.slice(0, 4)} •••• ${digits.slice(-4)}`;
}

/** Número completo para telas internas (drawer, operação). */
export function formatarTelefone(phone) {
    if (!phone) return '—';
    const digits = String(phone).replace(/\D/g, '');
    if (!digits) return '—';
    if (digits.length === 13 && digits.startsWith('55')) {
        return `+${digits.slice(0, 2)} (${digits.slice(2, 4)}) ${digits.slice(4, 9)}-${digits.slice(9)}`;
    }
    if (digits.length === 12 && digits.startsWith('55')) {
        return `+${digits.slice(0, 2)} (${digits.slice(2, 4)}) ${digits.slice(4, 8)}-${digits.slice(8)}`;
    }
    if (digits.length === 11) {
        return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
    }
    if (digits.length === 10) {
        return `(${digits.slice(0, 2)}) ${digits.slice(2, 6)}-${digits.slice(6)}`;
    }
    return digits;
}

export function rotuloTipo(tipo, t) {
    const map = {
        ad: t('ctwaPage.sourceTypes.ad'),
        post: t('ctwaPage.sourceTypes.post'),
        desconhecido: t('ctwaPage.sourceTypes.other'),
    };
    return map[tipo] || tipo;
}

export function periodoParaDatas(period) {
    const end = new Date();
    const start = new Date();

    switch (period) {
        case 'last-7-days':
            start.setDate(end.getDate() - 7);
            break;
        case 'last-14-days':
            start.setDate(end.getDate() - 14);
            break;
        case 'last-30-days':
            start.setDate(end.getDate() - 30);
            break;
        case 'last-90-days':
            start.setDate(end.getDate() - 90);
            break;
        default:
            start.setDate(end.getDate() - 30);
    }

    return {
        inicio: start.toISOString().split('T')[0],
        final: end.toISOString().split('T')[0],
    };
}

export function montarQuerysCtwa({ userId, clienteId, inicio, final }) {
    return `?periodo=null&usuario_id=${userId}&cliente_id=${clienteId}&inicio=${inicio}&final=${final}&limit=50`;
}

export function filtrarCanaisWhatsappBusiness(canais = []) {
    return canais.filter((canal) => canal.platform === 'whatsapp_business');
}

export function montarQuerysCtwaAnuncioChats({ querysBase, sourceId, page = 1, limit = 25 }) {
    const base = querysBase.replace(/([?&])limit=\d+/g, '$1').replace(/[?&]$/, '');
    const sep = base.includes('?') ? '&' : '?';
    return `${base}${sep}source_id=${encodeURIComponent(sourceId)}&page=${page}&limit=${limit}`;
}

export function montarQuerysCtwaAnuncios({ querysBase, page = 1, limit = 12, plataforma = 'all' }) {
    const base = querysBase.replace(/([?&])limit=\d+/g, '$1').replace(/[?&]$/, '');
    const sep = base.includes('?') ? '&' : '?';
    const plataformaParam = plataforma && plataforma !== 'all' ? `&plataforma=${encodeURIComponent(plataforma)}` : '';
    return `${base}${sep}page=${page}&limit=${limit}${plataformaParam}`;
}

export function previewAnuncio(anuncio) {
    if (!anuncio) return null;
    if (anuncio.media_type === 'video') return anuncio.thumbnail_url || anuncio.image_url || null;
    return anuncio.image_url || anuncio.thumbnail_url || null;
}

export function plataformaAnuncio(sourceUrl) {
    const url = String(sourceUrl || '').toLowerCase();
    if (url.includes('instagram.com')) return { id: 'instagram', label: 'Instagram' };
    if (url.includes('facebook.com') || url.includes('fb.me')) return { id: 'facebook', label: 'Facebook' };
    return { id: 'meta', label: 'Meta' };
}

export const PLATAFORMA_COR = {
    instagram: '#c13584',
    facebook: '#1877f2',
    meta: '#128c7e',
};

export function contarPorPlataforma(anuncios) {
    const contagem = { all: anuncios.length, instagram: 0, facebook: 0, meta: 0 };
    for (const item of anuncios) {
        const id = plataformaAnuncio(item.source_url).id;
        if (contagem[id] !== undefined) contagem[id] += 1;
    }
    return contagem;
}

export function filtrarPorPlataforma(anuncios, filtro) {
    if (!filtro || filtro === 'all') return anuncios;
    return anuncios.filter((item) => plataformaAnuncio(item.source_url).id === filtro);
}

export function truncarTexto(texto, max = 140) {
    if (!texto) return '';
    const limpo = String(texto).replace(/\s+/g, ' ').trim();
    if (limpo.length <= max) return limpo;
    return `${limpo.slice(0, max).trim()}…`;
}

export function rotuloMidia(mediaType, t) {
    const map = {
        image: t('ctwaPage.mediaTypes.image'),
        video: t('ctwaPage.mediaTypes.video'),
    };
    return map[mediaType] || mediaType || t('ctwaPage.mediaTypes.other');
}

/** Data da última entrada CTWA (contato capturado) para cards e lista. */
export function formatarUltimoContatoCapturado(data) {
    if (!data) return null;
    const m = moment(data);
    if (!m.isValid()) return null;
    return m.format(m.hours() || m.minutes() ? 'DD/MM/YY HH:mm' : 'DD/MM/YY');
}
